// frontend/services/RequirementService.js

import axiosInstance from './axiosInstance';
import { formatDate } from '../utils/dateUtils';
import { debugError } from '../utils/debugUtils';

// Function to get all requirements
export async function getRequirements(signal = null) {
  try {
    const response = await axiosInstance.get('/requirements/', {
      signal // Pass AbortController signal
    });
    
    // Response validation
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error('Invalid requirements data structure');
    }

    return response.data;
  } catch (error) {
    // Handle cancellation properly
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
      console.log('Requirements request was cancelled');
      return []; // Return empty array for cancelled requests
    }
    
    // Secure logging - only log error.name and status for security
    const errorInfo = {
      name: error.name || 'UnknownError',
      status: error.response?.status || 'No status',
      cancelled: error.code === 'ERR_CANCELED'
    };
    debugError('Error fetching requirements:', errorInfo);
    throw error;
  }
}

// Function to get a single requirement by ID
export async function getRequirement(id) {
  // Input validation
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }

  try {
    const response = await axiosInstance.get(`/requirements/${encodeURIComponent(id)}/`);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (!response.data.id || typeof response.data.name !== 'string') {
      throw new Error('Invalid requirement response structure');
    }

    return response.data;
  } catch (error) {
    debugError('Error fetching requirement:', error.name);
    throw error;
  }
}

// Function to create a new requirement
export async function createRequirement(requirementData) {
  // Input validation
  if (!requirementData || typeof requirementData !== 'object') {
    throw new Error('Invalid requirement data');
  }
  if (typeof requirementData.name !== 'string' || !requirementData.name.trim()) {
    throw new Error('Invalid requirement name');
  }
  if (typeof requirementData.amount !== 'string' || isNaN(parseFloat(requirementData.amount))) {
    throw new Error('Invalid amount');
  }
  if (!requirementData.next_due_date || isNaN(new Date(requirementData.next_due_date).getTime())) {
    throw new Error('Invalid next due date');
  }
  // Add validation for notes if necessary
  if (requirementData.notes && typeof requirementData.notes !== 'string') {
    throw new Error('Invalid notes');
  }

  try {
    const response = await axiosInstance.post('/requirements/', requirementData);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (!response.data.id || typeof response.data.name !== 'string') {
      throw new Error('Invalid requirement response structure');
    }

    return response.data;
  } catch (error) {
    console.error('Error creating requirement:', error.name);
    throw error;
  }
}

// Function to update an existing requirement
export async function updateRequirement(id, requirementData) {
  // Input validation
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }
  if (!requirementData || typeof requirementData !== 'object') {
    throw new Error('Invalid requirement data');
  }
  if (requirementData.name && (typeof requirementData.name !== 'string' || !requirementData.name.trim())) {
    throw new Error('Invalid requirement name');
  }
  if (requirementData.amount && (typeof requirementData.amount !== 'string' || isNaN(parseFloat(requirementData.amount)))) {
    throw new Error('Invalid amount');
  }
  if (requirementData.next_due_date && isNaN(new Date(requirementData.next_due_date).getTime())) {
    throw new Error('Invalid next due date');
  }
  if (requirementData.notes && typeof requirementData.notes !== 'string') {
    throw new Error('Invalid notes');
  }

  try {
    const response = await axiosInstance.put(`/requirements/${encodeURIComponent(id)}/`, requirementData);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (!response.data.id || typeof response.data.name !== 'string') {
      throw new Error('Invalid requirement response structure');
    }

    return response.data;
  } catch (error) {
    console.error('Error updating requirement:', error.name);
    throw error;
  }
}

/** Partial update — use after scoped amount apply so amount is not re-sent on PUT. */
export async function patchRequirement(id, requirementData) {
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }
  if (!requirementData || typeof requirementData !== 'object') {
    throw new Error('Invalid requirement data');
  }

  try {
    const response = await axiosInstance.patch(`/requirements/${encodeURIComponent(id)}/`, requirementData);
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    return response.data;
  } catch (error) {
    console.error('Error patching requirement:', error.name);
    throw error;
  }
}

// Function to update a requirement and its future bills
export async function updateRequirementSeries(id, requirementData) {
  // Input validation
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }
  if (!requirementData || typeof requirementData !== 'object') {
    throw new Error('Invalid requirement data');
  }

  try {
    const response = await axiosInstance.post(`/requirements/${encodeURIComponent(id)}/update_series/`, requirementData);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (!response.data.requirement || typeof response.data.requirement !== 'object') {
      throw new Error('Invalid requirement response structure');
    }

    return response.data;
  } catch (error) {
    console.error('Error updating requirement series:', error.name);
    throw error;
  }
}

// Preview which bills would be deleted by changing effective dates
export async function previewRequirementEffectiveChange(id, payload) {
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }
  try {
    const response = await axiosInstance.post(`/requirements/${encodeURIComponent(id)}/preview_effective_change/`, payload);
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid preview response');
    }
    if (!Array.isArray(response.data.impacted_bills)) {
      throw new Error('Invalid impacted bills payload');
    }
    return response.data;
  } catch (error) {
    console.error('Error previewing effective change:', error.name);
    throw error;
  }
}

// Function to delete a requirement
export async function deleteRequirement(id) {
  // Input validation
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }

  try {
    await axiosInstance.delete(`/requirements/${encodeURIComponent(id)}/`);
    return true;
  } catch (error) {
    console.error('Error deleting requirement:', error.name);
    throw error;
  }
}

// Function to generate a bill from a requirement
export async function generateBill(id, options = {}) {
  // Input validation
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }
  
  try {
    const response = await axiosInstance.post(
      `/requirements/${encodeURIComponent(id)}/generate_bill/`,
      options
    );
  
    // Response validation - check if the response has the expected structure
    if (!response.data) {
      throw new Error('Empty response data');
    }
  
    return response.data;
  } catch (error) {
    // Log only safe error information
    console.error('Error generating bill:', error.message);
    
    if (error.response) {
      // Just log the status code, which is safe
      console.error('Response status:', error.response.status);
      // Use appropriate user-facing error based on status
      if (error.response.status === 404) {
        throw new Error('Requirement not found');
      } else if (error.response.status >= 400 && error.response.status < 500) {
        throw new Error('Invalid request. Please check your inputs.');
      } else {
        throw new Error('Server error generating bill. Please try again.');
      }
    } else if (error.request) {
      // Network/connection error
      throw new Error('Network error. Please check your connection.');
    }
    
    // Generic error for anything else
    throw new Error('Failed to generate bill. Please try again.');
  }
}

// Function to generate all due bills
export async function generateAllDueBills(startDate, endDate) {
  const params = {};

  // Input validation for optional parameters
  if (startDate) {
    if (isNaN(new Date(startDate).getTime())) {
      throw new Error('Invalid start date');
    }
    params.start_date = formatDate(new Date(startDate));
  }

  if (endDate) {
    if (isNaN(new Date(endDate).getTime())) {
      throw new Error('Invalid end date');
    }
    params.end_date = formatDate(new Date(endDate));
  }

  try {
    const response = await axiosInstance.post('/requirements/generate_all_due/', params);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (!Array.isArray(response.data.bill_ids)) {
      throw new Error('Invalid bill generation response');
    }

    return response.data;
  } catch (error) {
    console.error('Error generating due bills:', error.name);
    throw error;
  }
}

// Function to get the next unpaid bill for a requirement
export async function getNextUnpaidBill(id) {
  // Input validation
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }

  try {
    const response = await axiosInstance.get(`/requirements/${encodeURIComponent(id)}/next_unpaid_bill/`);

    // Response validation
    if (!response.data) {
      throw new Error('Invalid response data');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching next unpaid bill:', error.name);
    throw error;
  }
}


// Function to get budget projections
export async function getBudgetProjection(months = 3) {
  // Input validation
  if (typeof months !== 'number' || months < 1 || months > 12) {
    throw new Error('Invalid months parameter. Must be between 1 and 12.');
  }

  try {
    const response = await axiosInstance.get(`/requirements/budget_projection/?months=${encodeURIComponent(months)}`);

    // Response validation
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid projection data. Expected an array.');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching budget projection:', error.name);
    throw error;
  }
}

// Function to get upcoming requirements
export async function getUpcomingRequirements() {
  try {
    const response = await axiosInstance.get('/requirements/upcoming_requirements/');

    // Response validation
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid upcoming requirements data. Expected an array.');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching upcoming requirements:', error.name);
    throw error;
  }
}

// Function to get related bills for a requirement
export async function getRelatedBills(id, options = { page: 1, direction: 'both' }) {
  // Input validation
  if (!id || typeof id !== 'number') {
    throw new Error('Invalid requirement ID');
  }

  try {
    const response = await axiosInstance.get(
      `/requirements/${encodeURIComponent(id)}/related_bills/`,
      { params: options }
    );

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching related bills:', error.name);
    throw error;
  }
}

// Function to update the order of requirements
export async function updateRequirementOrder(requirements) {
  try {
    const response = await axiosInstance.put('/requirements/update_order/', {
      requirements: requirements.map((req, index) => ({
        id: req.id,
        order: index
      }))
    });
    return response.data;
  } catch (error) {
    console.error('Error updating requirement order:', error.name);
    throw error;
  }
}

// Utility function to refresh bank accounts after a requirement update
export function refreshBankAccountsData() {
  // If we have a global window.refreshBankAccounts function, call it
  if (typeof window.refreshBankAccounts === 'function') {
    return window.refreshBankAccounts();
  } else {
    return Promise.resolve();
  }
}

export async function getSeriesHistory(requirementId) {
  const response = await axiosInstance.get(
    `/requirements/${encodeURIComponent(requirementId)}/series_history/`
  );
  return response.data;
}

export async function previewScopedAmountChange(requirementId, payload) {
  const response = await axiosInstance.post(
    `/requirements/${encodeURIComponent(requirementId)}/scoped_amount_preview/`,
    payload
  );
  return response.data;
}

export async function applyScopedAmountChange(requirementId, payload) {
  const response = await axiosInstance.post(
    `/requirements/${encodeURIComponent(requirementId)}/scoped_amount_apply/`,
    payload
  );
  return response.data;
}

export async function previewScopedInactiveChange(requirementId, payload) {
  const response = await axiosInstance.post(
    `/requirements/${encodeURIComponent(requirementId)}/scoped_inactive_preview/`,
    payload
  );
  return response.data;
}

export async function applyScopedInactiveChange(requirementId, payload) {
  const response = await axiosInstance.post(
    `/requirements/${encodeURIComponent(requirementId)}/scoped_inactive_apply/`,
    payload
  );
  return response.data;
}

/** Patch name/notes/description on all segments from a fortnight start onward (indefinite scope). */
export async function patchMetadataFromFortnight(requirementId, fromFortnightDate, fields) {
  const response = await axiosInstance.post(
    `/requirements/${encodeURIComponent(requirementId)}/patch_metadata_from/`,
    { from_fortnight_date: fromFortnightDate, ...fields }
  );
  return response.data;
}

export async function previewRequirementTypeConversion(requirementId, payload) {
  if (!requirementId || typeof requirementId !== 'number') {
    throw new Error('Invalid requirement ID');
  }
  const response = await axiosInstance.post(
    `/requirements/${encodeURIComponent(requirementId)}/convert_type_preview/`,
    payload
  );
  return response.data;
}

export async function applyRequirementTypeConversion(requirementId, payload) {
  if (!requirementId || typeof requirementId !== 'number') {
    throw new Error('Invalid requirement ID');
  }
  const response = await axiosInstance.post(
    `/requirements/${encodeURIComponent(requirementId)}/convert_type_apply/`,
    payload
  );
  return response.data;
}
